// Turf Talk web telemetry — a single, reusable, NON-BLOCKING helper.
// Contract: POST /api/v1/telemetry/events { events:[EventIn] } where
// EventIn = { event_id, name, surface, ts?, session_id?, game_id?, room_id?,
// duration_ms?, completed? }. Fired ONLY on real, successful user actions.
// It NEVER throws into caller code, NEVER blocks a user action, and stores no
// message/media content, IP, device fingerprint, or extra PII.
import { TurfTalk } from "../api/client";

const SURFACE = "web";
const MAX_QUEUE = 100;
const BATCH = 50;
const FLUSH_MS = 4000;

let queue = [];
let timer = null;
let sessionId = null;
let sessionStartMs = 0;

// Stable, idempotent event id. Reused verbatim on retries so the backend
// de-dupes rather than double-counting.
export function newEventId(name = "evt") {
  const clean = String(name).replace(/[^a-z0-9_]/gi, "").slice(0, 20) || "evt";
  return `evt-${clean}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`.slice(0, 80);
}

export function clampDuration(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return undefined;
  return Math.max(0, Math.min(86400000, Math.round(Number(ms))));
}

// Build a contract-exact EventIn from a name + optional fields.
export function buildEvent(name, fields = {}) {
  const e = {
    event_id: fields.event_id || newEventId(name),
    name: String(name).slice(0, 40),
    surface: SURFACE,
    ts: new Date().toISOString(),
  };
  const sid = fields.session_id || sessionId;
  if (sid) e.session_id = String(sid).slice(0, 80);
  if (fields.game_id) e.game_id = String(fields.game_id).slice(0, 80);
  if (fields.room_id) e.room_id = String(fields.room_id).slice(0, 80);
  const d = clampDuration(fields.duration_ms);
  if (d !== undefined) e.duration_ms = d;
  if (typeof fields.completed === "boolean") e.completed = fields.completed;
  return e;
}

async function flush() {
  if (!queue.length) return;
  const batch = queue.slice(0, BATCH);
  try {
    await TurfTalk.telemetryEvents(batch);
    queue = queue.slice(batch.length); // drop only on success → idempotent retry
  } catch {
    // silent: keep the batch queued for the next flush; never disrupt the user
  }
}

function ensureTimer() {
  if (timer || typeof setInterval === "undefined") return;
  timer = setInterval(flush, FLUSH_MS);
}

// Queue one event. Silent on any error.
export function track(name, fields = {}) {
  try {
    queue.push(buildEvent(name, fields));
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    ensureTimer();
  } catch {
    /* silent */
  }
}

export function startSession() {
  if (sessionId) return sessionId;
  sessionId = newEventId("sess");
  sessionStartMs = Date.now();
  track("session_start", { session_id: sessionId });
  return sessionId;
}

export function endSession() {
  if (!sessionId) return;
  track("session_end", { session_id: sessionId, duration_ms: Date.now() - sessionStartMs });
  flush();
  sessionId = null;
  sessionStartMs = 0;
}

// Convenience wrappers — one per instrumented, successful action.
export const trackGameView = (game_id) => track("game_view", { game_id });
export const trackRoomJoin = (room_id) => track("room_join", { room_id });
export const trackRoomLeave = (room_id, duration_ms) => track("room_leave", { room_id, duration_ms });
export const trackPickSubmitted = (game_id) => track("pick_submitted", { game_id });
// message_sent carries metadata ONLY (room id) — never the message body.
export const trackMessageSent = (room_id) => track("message_sent", room_id ? { room_id } : {});
export const trackShowStart = (game_id) => track("character_show_start", { game_id });
export const trackShowComplete = (game_id, duration_ms) => track("character_show_complete", { game_id, duration_ms, completed: true });
export const trackHostStart = (game_id, room_id) => track("host_session_start", { game_id, room_id });
export const trackHostEnd = (game_id, room_id, duration_ms) => track("host_session_end", { game_id, room_id, duration_ms });

// Test-only hooks.
export function __reset() {
  queue = [];
  if (timer) { clearInterval(timer); timer = null; }
  sessionId = null;
  sessionStartMs = 0;
}
export function __queue() { return queue.slice(); }
export function __flushNow() { return flush(); }
